import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { request } from 'utils/request';
import { IRestApiResponse } from 'interfaces';
import { coreSliceActions } from 'base/core/slice';
import {
  IExpertCertificateBulkIssuePayload,
  IExpertCertificateIssuePayload,
  IGetExpertCertificatesPayload,
  IRevokeExpertCertificatePayload,
} from './interface';
import { expertCertificatesActions } from './slice';
import API from './constants';
import { selectExpertCertificateExpertId } from './selector';

const buildCertificateQueryParams = (
  payload?: Partial<IGetExpertCertificatesPayload>,
) => {
  const params = new URLSearchParams();

  if (payload?.page !== undefined) {
    params.append('page', payload.page.toString());
  }
  if (payload?.limit !== undefined) {
    params.append('limit', payload.limit.toString());
  }
  if (payload?.user?.trim()) {
    params.append('user', payload.user.trim());
  }
  if (payload?.status) {
    params.append('status', payload.status.toString());
  }
  if (payload?.issued !== undefined) {
    params.append('issued', payload.issued.toString());
  }
  if (payload?.completion) {
    params.append('completion', payload.completion.toString());
  }

  return params;
};

const buildPathWithQueryParams = (path: string, params: URLSearchParams) => {
  const queryString = params.toString();

  return queryString ? `${path}?${queryString}` : path;
};

export function* getExpertCertificatesGenerator(
  { payload }: PayloadAction<IGetExpertCertificatesPayload>,
): any {
  try {
    const expertId = yield select(selectExpertCertificateExpertId);
    const params = buildCertificateQueryParams(payload);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: buildPathWithQueryParams(
          `${API.GET_EXPERT_CERTIFICATES.path}/${expertId}`,
          params,
        ),
        method: API.GET_EXPERT_CERTIFICATES.method,
      },
      null,
      true,
    );

    if (response.statusCode === 200) {
      yield put(expertCertificatesActions.getExpertCertificatesSucceeded(response.data));
    } else {
      yield put(expertCertificatesActions.getExpertCertificatesFailed());
    }
  } catch (error) {
    yield put(expertCertificatesActions.getExpertCertificatesFailed());
  }
}

export function* issueExpertCertificateGenerator(
  { payload }: PayloadAction<IExpertCertificateIssuePayload>,
): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.ISSUE_SESSION_CERTIFICATE.path}/${payload.sessionId}`,
        method: API.ISSUE_SESSION_CERTIFICATE.method,
      },
      { userId: payload.userId },
      true,
    );

    if (response.statusCode === 200 || response.statusCode === 201) {
      yield put(expertCertificatesActions.issueExpertCertificateSucceeded());
      yield put(coreSliceActions.setMessage({
        message: 'Certificate issued successfully',
        success: true,
      }));
      yield put(expertCertificatesActions.triggerRefetchExpertCertificates());
    } else {
      const failureMessage = response.message || 'Certificate issuance failed';
      yield put(expertCertificatesActions.issueExpertCertificateFailed());
      yield put(coreSliceActions.setMessage({
        message: failureMessage,
        success: false,
      }));
    }
  } catch (error) {
    const failureMessage = error instanceof Error && error.message
      ? error.message
      : 'Certificate issuance failed';
    yield put(expertCertificatesActions.issueExpertCertificateFailed());
    yield put(coreSliceActions.setMessage({
      message: failureMessage,
      success: false,
    }));
  }
}

export function* bulkIssueExpertCertificatesGenerator(
  { payload }: PayloadAction<IExpertCertificateBulkIssuePayload>,
): any {
  try {
    const groupedUserIds = payload.certificates.reduce<Record<string, string[]>>(
      (certificateGroups, certificateItem) => {
        const sessionCertificates = certificateGroups[certificateItem.sessionId] ?? [];
        if (!sessionCertificates.includes(certificateItem.userId)) {
          sessionCertificates.push(certificateItem.userId);
        }

        return {
          ...certificateGroups,
          [certificateItem.sessionId]: sessionCertificates,
        };
      },
      {},
    );

    const responses: IRestApiResponse[] = yield all(
      Object.entries(groupedUserIds).map(([sessionId, userIds]) => call(
        request,
        {
          path: `${API.BULK_ISSUE_SESSION_CERTIFICATE.path}/${sessionId}`,
          method: API.BULK_ISSUE_SESSION_CERTIFICATE.method,
        },
        { userIds },
        true,
      )),
    );

    if (responses.every((response) => response.statusCode === 200 || response.statusCode === 201)) {
      yield put(expertCertificatesActions.bulkIssueExpertCertificatesSucceeded());
      yield put(coreSliceActions.setMessage({
        message: 'Certificates issued successfully',
        success: true,
      }));
      yield put(expertCertificatesActions.triggerRefetchExpertCertificates());
    } else {
      const failureMessage = responses.find(
        (response) => !(response.statusCode === 200 || response.statusCode === 201),
      )?.message || 'Bulk certificate issuance failed';
      yield put(expertCertificatesActions.bulkIssueExpertCertificatesFailed());
      yield put(coreSliceActions.setMessage({
        message: failureMessage,
        success: false,
      }));
    }
  } catch (error) {
    const failureMessage = error instanceof Error && error.message
      ? error.message
      : 'Bulk certificate issuance failed';
    yield put(expertCertificatesActions.bulkIssueExpertCertificatesFailed());
    yield put(coreSliceActions.setMessage({
      message: failureMessage,
      success: false,
    }));
  }
}

export function* revokeExpertCertificateGenerator(
  { payload }: PayloadAction<IRevokeExpertCertificatePayload>,
): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.REVOKE_CERTIFICATE.path}/${payload.certificateId}`,
        method: API.REVOKE_CERTIFICATE.method,
      },
      { reason: payload.reason },
      true,
    );

    if (response.statusCode === 200 || response.statusCode === 201) {
      yield put(expertCertificatesActions.revokeExpertCertificateSucceeded());
      yield put(coreSliceActions.setMessage({
        message: 'Certificate revoked successfully',
        success: true,
      }));
      yield put(expertCertificatesActions.triggerRefetchExpertCertificates());
    } else {
      yield put(expertCertificatesActions.revokeExpertCertificateFailed());
      yield put(coreSliceActions.setMessage({
        message: 'Certificate revocation failed',
        success: false,
      }));
    }
  } catch (error) {
    yield put(expertCertificatesActions.revokeExpertCertificateFailed());
    yield put(coreSliceActions.setMessage({
      message: 'Certificate revocation failed',
      success: false,
    }));
  }
}

export function* expertCertificatesSaga() {
  yield all([
    takeLatest(
      expertCertificatesActions.getExpertCertificates.type,
      getExpertCertificatesGenerator,
    ),
    takeLatest(
      expertCertificatesActions.issueExpertCertificate.type,
      issueExpertCertificateGenerator,
    ),
    takeLatest(
      expertCertificatesActions.bulkIssueExpertCertificates.type,
      bulkIssueExpertCertificatesGenerator,
    ),
    takeLatest(
      expertCertificatesActions.revokeExpertCertificate.type,
      revokeExpertCertificateGenerator,
    ),
  ]);
}

export default expertCertificatesSaga;
